<template>
  <div>
    <!-- Success callout when integration is complete -->
    <b-alert
      v-if="isIntegrationComplete"
      show
      variant="success"
      class="mb-4"
    >
      <h5 class="mb-2">
        🎉 Ringba Integration Complete!
      </h5>
      <p class="mb-0">
        You can now trigger Ring Tree Target creation for approved offers and we'll set it up for you in your selected Ringba Account.
      </p>
    </b-alert>


    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(onSubmitToken)"
      >
        <legend class="legend-title">
          <TaskCheckCircle
            class="mr-1"
            :show-check="integrationProgress.apiTokenAdded"
          />
          Enter Ringba API Token
        </legend>
        
        <template v-if="hasDefaultToken">
          <div class="form-group form-row">
            <label class="col-lg-3 col-form-label text-left text-sm-right custom-form-input-label--internal pr-4">Ringba API Token</label>
            <div class="col-lg-6">
              <b-button
                variant="danger"
                size="sm"
                @click="replaceToken"
              >
                Replace Token
              </b-button>
            </div>
          </div>
        </template>
        <BaseFormInput
          v-else
          id="ringba-api-token"
          v-model="apiToken"
          horizontal
          small
          name="Ringba API Token"
          data-cy="input-ringba-token"
          internal-platform-input
          placeholder="Enter your Ringba API token"
          label="Ringba API Token"
          type="text"
          width="12"
          width-lg="6"
          width-xl="6"
          rules="required"
          required
          has-tooltip
        >
          <template #helpText>
            <div class="mt-1">
              <i>
                <a
                  href="https://support.ringba.com/hc/en-us/articles/31661837696023-API-Access-Tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                >Click here</a> see this guide to help you generate an API Token in Ringba. Once generated, copy and paste the token here.
              </i>
            </div>
          </template>
        </BaseFormInput>

        <BaseButtonLoader
          v-if="!hasDefaultToken"
          horizontal
          data-cy="button-save-ringba-token"
          internal-platform-input
          variant="primary"
          :disabled="invalid"
          :loading="isLoading"
          type="submit"
          size="sm"
        >
          Save Token
        </BaseButtonLoader>
      </b-form>
    </validation-observer>

    <validation-observer
      ref="accountFormValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        class="mt-5"
        @submit.stop.prevent="handleSubmit(onSubmitAccountSelection)"
      >
        <legend
          class="legend-title"
          :class="{'mb-2': !integrationProgress.apiTokenAdded}"
        >
          <TaskCheckCircle
            class="mr-1"
            :show-check="integrationProgress.ringbaAccountSelected"
          />
          Select Ringba Account
        </legend>
        
        <BaseFormSelect
          v-if="hasDefaultToken"
          id="ringba-account"
          v-model="selectedAccountId"
          horizontal
          small
          name="Ringba Account"
          data-cy="select-ringba-account"
          internal-platform-input
          label="Ringba Account"
          width="12"
          width-lg="5"
          width-xl="5"
          rules="required"
          required
          has-tooltip
          :options="ringbaAccountsList"
          text-field="name"
          value-field="id"
          placeholder="Select a Ringba account"
          @change="onRingbaAccountChange"
        />
        <div
          v-else
          class="ml-4 text-muted"
        >
          <i>
            Please add a Ringba API token to select Ringba account.
          </i>
        </div>

        <!-- RTT Not Enabled Warning -->
        <b-alert
          v-if="showRTTWarning"
          show
          variant="danger"
          class="ml-4 mt-2"
        >
          <strong>RTT Creation Request Unavailable</strong>
          <p class="mb-0 mt-1">
            You need to have the RTT feature enabled or switch to a billing plan that supports Ring Tree Targets in your Ringba account.
          </p>
        </b-alert>

        <BaseButtonLoader
          v-if="hasDefaultToken"
          horizontal
          data-cy="button-save-ringba-token"
          internal-platform-input
          variant="primary"
          :disabled="invalid"
          :loading="isLoading"
          type="submit"
          size="sm"
        >
          Save
        </BaseButtonLoader>
      </b-form>
    </validation-observer>

    <!-- Replace Token Modal -->
    <b-modal
      id="replace-token-modal"
      ref="replace-token-modal"
      size="lg"
      title="Replace API Token"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      @hidden="resetReplaceModal"
    >
      <template #default>
        <p class="mb-4">
          Please enter and test your new API token before replacing the existing one.
        </p>
        
        <validation-observer
          ref="modalFormValidation"
          v-slot="{ handleSubmit, invalid }"
        >
          <b-form
            novalidate
            @submit.stop.prevent="handleSubmit(testNewToken)"
          >
            <BaseFormInput
              id="new-ringba-api-token"
              v-model="newApiToken"
              horizontal
              small
              name="New Ringba API Token"
              data-cy="input-new-ringba-token"
              internal-platform-input
              placeholder="Enter your new Ringba API token"
              label="New API Token"
              type="text"
              width="8"
              width-xs="12"
              rules="required"
              required
            />

            <div class="form-group row">
              <div class="col-lg-3" />
              <div class="col-lg-9">
                <BaseButtonLoader
                  data-cy="button-test-token"
                  variant="success"
                  :disabled="invalid || !newApiToken"
                  :loading="isTestingToken"
                  type="submit"
                  size="sm"
                >
                  Test Token
                </BaseButtonLoader>
              </div>
            </div>

            <div
              v-if="tokenTested"
              class="form-group row"
            >
              <div class="col-lg-3" />
              <div class="col-lg-9">
                <b-alert
                  :show="true"
                  :variant="tokenTestSuccessful ? 'success' : 'danger'"
                >
                  <span v-if="tokenTestSuccessful">Token verified successfully!</span>
                  <span v-else>Token verification failed. Please check your token and try again.</span>
                </b-alert>
              </div>
            </div>
          </b-form>
        </validation-observer>
      </template>

      <template #modal-footer>
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="onCancelReplaceToken"
        >
          Cancel
        </b-button>
        <BaseButtonLoader
          variant="primary"
          size="sm"
          :disabled="!tokenTestSuccessful"
          :loading="isReplacingToken"
          @click="confirmReplaceToken"
        >
          Replace Token
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { showSuccessMessage, showErrorMessage } from '@/notification-utils'
import { generateTokenName } from '@/store/modules/ringba-tokens.store'
import TaskCheckCircle from '@/components/common/TaskCheckCircle.vue'

export default {
  name: 'ManageRingbaApiToken',

  components: {
    TaskCheckCircle
  },

  data() {
    return {
      apiToken: '',
      selectedAccountId: null,
      isLoading: false,
      isFetchingAccounts: false,
      integrationProgress: {
        apiTokenAdded: false,
        ringbaAccountSelected: false,
      },
      // Modal related data
      newApiToken: '',
      isTestingToken: false,
      isReplacingToken: false,
      tokenTested: false,
      tokenTestSuccessful: false,

      oldTokenForDeletion: null,

      // RTT enabled check
      isCheckingRTTEnabled: false,
      hasCheckedRTTEnabled: false,
      isRTTEnabled: true, // Default to true until checked
    }
  },
  
  computed: {
    ...mapGetters('auth', ['accountId', 'ringbaAccountId']),
    ...mapGetters('ringbaTokens', ['defaultToken']),
    ...mapState('ringbaTokens', {
      allTokens: 'tokens'
    }),
    ...mapGetters('sellerOffer', ['canCreateRingTreeTarget']),
    ...mapState('ringbaAccounts', {
      ringbaAccountsList: 'accounts'
    }),

    hasDefaultToken() {
      return !!this.defaultToken
    },

    isIntegrationComplete() {
      return this.integrationProgress.apiTokenAdded && this.integrationProgress.ringbaAccountSelected
    },

    showRTTWarning() {
      return this.hasCheckedRTTEnabled && !this.isRTTEnabled
    }
  },

  watch: {
    hasDefaultToken: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          this.$set(this.integrationProgress, 'apiTokenAdded', true)
          // Fetch Ringba accounts when default token exists
          await this.loadRingbaAccounts()
        }
      }
    },
    
    ringbaAccountsList: {
      immediate: true,
      async handler(newValue) {
        // Check if the stored ringbaAccountId exists in the fetched accounts list
        if (this.ringbaAccountId && newValue && newValue.length > 0) {
          const accountExists = newValue.some(account => account.id === this.ringbaAccountId)

          if (accountExists && this.hasDefaultToken) {
            this.$set(this.integrationProgress, 'ringbaAccountSelected', true)
            // Set the selected account ID to match the stored value
            this.selectedAccountId = this.ringbaAccountId
            // Check if RTT is enabled for this account
            await this.checkRTTEnabled()
          }
        }
      }
    }
  },
  
  methods: {
    ...mapActions('ringbaTokens', ['createToken', 'deleteToken', 'setDefaultToken']),
    ...mapActions('ringbaAccounts', ['fetchAccounts']),
    ...mapActions('account', ['setRingbaAccount']),
    ...mapActions('sellerOffer', ['checkCanCreateRingTreeTarget']),

    async onRingbaAccountChange(newAccountId) {
      // Reset RTT check when account selection changes
      this.hasCheckedRTTEnabled = false
      this.isRTTEnabled = true

      // If integration is already complete, save and check immediately
      if (this.integrationProgress.ringbaAccountSelected && newAccountId) {
        this.isLoading = true
        try {
          await this.setRingbaAccount(newAccountId)
          await this.checkRTTEnabled()
        } catch (error) {
          // Silently handle errors
        } finally {
          this.isLoading = false
        }
      }
    },

    async checkRTTEnabled() {
      this.isCheckingRTTEnabled = true
      try {
        await this.checkCanCreateRingTreeTarget()
        this.isRTTEnabled = this.canCreateRingTreeTarget
        this.hasCheckedRTTEnabled = true
      } catch (error) {
        this.isRTTEnabled = false
        this.hasCheckedRTTEnabled = true
      } finally {
        this.isCheckingRTTEnabled = false
      }
    },

    async onSubmitToken() {
      this.isLoading = true

      try {
        const tokenDetails = {
          name: generateTokenName('Default Token'),
          apiToken: this.apiToken,
          setAsDefault: true
        }

        await this.createToken({
          accountId: this.accountId,
          tokenDetails
        })

        // Clear form
        this.apiToken = ''
        
        // Show success message
        showSuccessMessage('Ringba API token saved successfully!')

        this.$set(this.integrationProgress, 'apiTokenAdded', true)
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to save API token. Please try again.'
        showErrorMessage(errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    async loadRingbaAccounts() {
      this.isFetchingAccounts = true
      try {
        await this.fetchAccounts({ accountId: this.accountId })
      } catch (error) {
        showErrorMessage('Failed to fetch Ringba accounts. Please refresh and try again.')
      } finally {
        this.isFetchingAccounts = false
      }
    },

    async onSubmitAccountSelection() {
      this.isLoading = true

      try {
        // Save the selected Ringba account
        await this.setRingbaAccount(this.selectedAccountId)

        // Show success message
        showSuccessMessage('Ringba account selected successfully!')

        this.$set(this.integrationProgress, 'ringbaAccountSelected', true)

        // Check if RTT is enabled for this account
        await this.checkRTTEnabled()

      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to save account selection. Please try again.'
        showErrorMessage(errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    replaceToken() {
      this.$bvModal.show('replace-token-modal')
    },

    async revertToOldToken() {
      if (!this.oldTokenForDeletion?.id) return

      await this.setDefaultToken({
        accountId: this.accountId,
        tokenId: this.oldTokenForDeletion.id
      }).catch(() => {})

      // Delete all tokens except the old one
      const tokensToDelete = this.allTokens.filter(t => t.id !== this.oldTokenForDeletion.id)
      await Promise.all(
        tokensToDelete.map(token =>
          this.deleteToken({
            accountId: this.accountId,
            tokenId: token.id
          }).catch(() => {})
        )
      )
    },

    async testNewToken() {
      this.isTestingToken = true
      this.tokenTested = false
      this.tokenTestSuccessful = false
      this.oldTokenForDeletion = { ...this.defaultToken }

      try {
        await this.createToken({
          accountId: this.accountId,
          tokenDetails: {
            name: generateTokenName('Default Token'),
            apiToken: this.newApiToken,
            setAsDefault: true
          }
        })

        await this.fetchAccounts({ accountId: this.accountId })
        this.tokenTestSuccessful = true
      } catch {
        await this.revertToOldToken()
        showErrorMessage('Token verification failed. Please check your token.')
      } finally {
        this.tokenTested = true
        this.isTestingToken = false
      }
    },

    async confirmReplaceToken() {
      this.isReplacingToken = true

      try {
        if (this.oldTokenForDeletion?.id) {
          await this.deleteToken({
            accountId: this.accountId,
            tokenId: this.oldTokenForDeletion.id
          })
        }

        // Clear flag so resetReplaceModal doesn't revert on successful confirm
        this.tokenTestSuccessful = false

        showSuccessMessage('API token replaced successfully!')
        this.$bvModal.hide('replace-token-modal')
        await this.fetchAccounts({ accountId: this.accountId })
      } catch (error) {
        showErrorMessage(error.response?.data?.message || 'Failed to replace token. Please try again.')
      } finally {
        this.isReplacingToken = false
      }
    },

    onCancelReplaceToken() {
      this.$bvModal.hide('replace-token-modal')
    },

    async resetReplaceModal() {
      // Revert to old token if test was successful but user didn't confirm
      if (this.tokenTestSuccessful) {
        await this.revertToOldToken()
      }

      this.newApiToken = ''
      this.tokenTested = false
      this.tokenTestSuccessful = false
      this.isTestingToken = false
      this.isReplacingToken = false
      this.oldTokenForDeletion = null
    }
  }
}
</script>

<style lang="scss" scoped>
</style>